import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FiArrowLeft, FiRotateCcw, FiUploadCloud } from 'react-icons/fi';
import api from '../config/api';
import { usePermissions } from '../context/PermissionsContext';
import { usePageTitle } from '../context/PageChromeContext';
import IconButton from '../components/IconButton';
import LoadingButton from '../components/LoadingButton';
import { StatusBadge, formatCents as money } from '../components/shared/Ui';
import { invalidateCreditNotes, queryKeys, staleTimes } from '../config/queryKeys';
import { appAlert, appConfirm } from '../utils/appDialog';
import './Services.css';
import '../components/erp/Erp.css';

const emptyForm = {
  sales_document_id: '',
  amount: '',
  reason: '',
  credit_date: new Date().toISOString().slice(0, 10),
  currency: 'USD',
};

/**
 * Full-page credit note create / view (Sales → Credits).
 */
const CreditNoteDetails = () => {
  const { creditNoteId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { hasPermission } = usePermissions();
  const isNew = !creditNoteId || creditNoteId === 'new';
  const canManage = hasPermission('erp_selling_manage');
  const canPost = hasPermission('erp_journals_post');

  const [form, setForm] = useState(emptyForm);

  const {
    data: note,
    isLoading: loadingNote,
    isError,
    error,
  } = useQuery(
    ['credit-note', creditNoteId],
    () => api.get(`/selling/credit-notes/${creditNoteId}`).then((r) => r.data),
    { enabled: !isNew && Boolean(creditNoteId), staleTime: 0 }
  );

  const { data: invoices = [] } = useQuery(
    queryKeys.salesDocuments('invoice'),
    () =>
      api
        .get('/services/sales/documents', { params: { doc_type: 'invoice' } })
        .then((r) => r.data),
    { enabled: isNew && canManage, staleTime: staleTimes.lists }
  );

  const { data: currencies = [] } = useQuery(
    'currencies-active',
    () => api.get('/currencies').then((r) => r.data),
    { staleTime: 60_000 }
  );

  const invoiceRows = useMemo(
    () => (invoices || []).filter((inv) => !['void', 'draft'].includes(inv.status)),
    [invoices]
  );

  const selectedInvoice = useMemo(
    () => invoiceRows.find((inv) => String(inv.id) === String(form.sales_document_id)),
    [invoiceRows, form.sales_document_id]
  );

  useEffect(() => {
    if (isNew && selectedInvoice?.currency) {
      setForm((prev) => ({ ...prev, currency: selectedInvoice.currency || 'USD' }));
    }
  }, [isNew, selectedInvoice?.currency, selectedInvoice?.id]);

  usePageTitle(
    isNew
      ? 'New credit note'
      : note?.credit_number
        ? note.credit_number
        : 'Credit note'
  );

  const createMutation = useMutation(
    async () =>
      (
        await api.post('/selling/credit-notes', {
          sales_document_id: Number(form.sales_document_id),
          amount_cents: Math.round(Number(form.amount || 0) * 100),
          reason: form.reason,
          credit_date: form.credit_date || undefined,
          currency: form.currency || undefined,
        })
      ).data,
    {
      onSuccess: (created) => {
        invalidateCreditNotes(qc);
        navigate(`/sales/credit-notes/${created.id}`, { replace: true });
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const postMutation = useMutation(
    async () => (await api.post(`/selling/credit-notes/${creditNoteId}/post`)).data,
    {
      onSuccess: () => {
        invalidateCreditNotes(qc);
        qc.invalidateQueries(['credit-note', creditNoteId]);
        qc.invalidateQueries(['erp-report']);
        void appAlert('Credit note posted to GL.');
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const unpostMutation = useMutation(
    async () => (await api.post(`/selling/credit-notes/${creditNoteId}/unpost`)).data,
    {
      onSuccess: () => {
        invalidateCreditNotes(qc);
        qc.invalidateQueries(['credit-note', creditNoteId]);
        qc.invalidateQueries(['erp-report']);
        void appAlert('Credit note reverted to draft.');
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const backToList = () => navigate('/erp/selling?tab=credits');

  if (!isNew && loadingNote) {
    return <div className="loading">Loading credit note…</div>;
  }

  if (!isNew && (isError || !note)) {
    return (
      <div className="svc-package-page list-page erp-page">
        <div className="svc-package-toolbar page-toolbar">
          <IconButton icon={FiArrowLeft} label="Back to Sales" onClick={backToList} />
        </div>
        <div className="svc-package-page-card">
          <div className="error">
            {error?.response?.data?.error || error?.message || 'Credit note not found'}
          </div>
        </div>
      </div>
    );
  }

  if (!isNew) {
    return (
      <div className="svc-package-page list-page erp-page">
        <div className="svc-package-toolbar page-toolbar">
          <IconButton icon={FiArrowLeft} label="Back to Sales" onClick={backToList} />
        </div>
        <div className="svc-package-page-card">
          <div className="svc-package-page-header">
            <h1>
              <FiRotateCcw aria-hidden /> {note.credit_number}
            </h1>
            <p className="page-subtitle">
              Credit against {note.invoice_number || `invoice #${note.sales_document_id || '—'}`}
              {note.credit_date ? ` · ${note.credit_date}` : ''}
            </p>
          </div>

          <div className="erp-form-grid">
            <div className="erp-field">
              <label>Status</label>
              <div className="erp-readonly-value"><StatusBadge status={note.status} /></div>
            </div>
            <div className="erp-field">
              <label>Currency</label>
              <div className="erp-readonly-value erp-code">{note.currency || 'USD'}</div>
            </div>
            <div className="erp-field">
              <label>Amount</label>
              <div className="erp-readonly-value erp-num">{money(note.amount_cents, note.currency)}</div>
            </div>
            <div className="erp-field">
              <label>Date</label>
              <div className="erp-readonly-value">{note.credit_date || '—'}</div>
            </div>
            <div className="erp-field">
              <label>Customer</label>
              <div className="erp-readonly-value">
                {note.customer_billing_name ||
                  (note.customer_id ? `#${note.customer_id}` : '—')}
              </div>
            </div>
            <div className="erp-field">
              <label>Invoice</label>
              <div className="erp-readonly-value">
                {note.sales_document_id ? (
                  <Link to={`/sales/documents/${note.sales_document_id}`}>
                    <span className="erp-code">
                      {note.invoice_number || `Invoice #${note.sales_document_id}`}
                    </span>
                  </Link>
                ) : (
                  '—'
                )}
              </div>
            </div>
            <div className="erp-field" style={{ gridColumn: '1 / -1' }}>
              <label>Reason</label>
              <div className="erp-readonly-value">{note.reason || '—'}</div>
            </div>
            <div className="erp-field">
              <label>GL journal</label>
              <div className="erp-readonly-value erp-code">
                {note.journal_entry_number ||
                  (note.status === 'posted' ? 'Posted' : 'Not posted')}
              </div>
            </div>
          </div>

          <div className="svc-package-page-actions">
            <button type="button" className="btn btn-secondary" onClick={backToList}>
              Back
            </button>
            {note.status !== 'posted' && canPost ? (
              <LoadingButton
                type="button"
                className="btn btn-primary"
                icon={FiUploadCloud}
                loading={postMutation.isLoading}
                onClick={() => postMutation.mutate()}
              >
                Post to GL
              </LoadingButton>
            ) : null}
            {note.status === 'posted' && canPost ? (
              <LoadingButton
                type="button"
                className="btn btn-secondary"
                icon={FiRotateCcw}
                loading={unpostMutation.isLoading}
                onClick={async () => {
                  const ok = await appConfirm(
                    'Revert this credit note to draft? Its GL entry will leave the ledger so you can correct it and post again.'
                  );
                  if (ok) unpostMutation.mutate();
                }}
              >
                Revert to draft
              </LoadingButton>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  if (!canManage) {
    return (
      <div className="svc-package-page list-page erp-page">
        <div className="svc-package-toolbar page-toolbar">
          <IconButton icon={FiArrowLeft} label="Back" onClick={backToList} />
        </div>
        <div className="svc-package-page-card">
          <div className="error">You do not have permission to create credit notes.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="svc-package-page list-page erp-page">
      <div className="svc-package-toolbar page-toolbar">
        <IconButton icon={FiArrowLeft} label="Back to Sales" onClick={backToList} />
      </div>

      <div className="svc-package-page-card">
        <div className="svc-package-page-header">
          <h1>
            <FiRotateCcw aria-hidden /> New credit note
          </h1>
          <p className="page-subtitle">
            Credits reverse part or all of an issued customer invoice. Currency follows the invoice
            (required for GL posting).
          </p>
        </div>

        <form
          className="svc-package-form"
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate();
          }}
        >
          <div className="erp-form-grid">
            <div className="erp-field">
              <label htmlFor="cn-invoice">Invoice</label>
              <select
                id="cn-invoice"
                required
                value={form.sales_document_id}
                onChange={(e) =>
                  setForm({
                    ...form,
                    sales_document_id: e.target.value,
                  })
                }
              >
                <option value="">— Select invoice —</option>
                {invoiceRows.map((invoice) => (
                  <option key={invoice.id} value={invoice.id}>
                    {invoice.number} · {invoice.client_name}
                    {invoice.currency ? ` · ${invoice.currency}` : ''}
                    {invoice.total != null ? ` · ${invoice.total}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="erp-field form-group--currency">
              <label htmlFor="cn-currency">Currency</label>
              <select
                id="cn-currency"
                value={form.currency || 'USD'}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                disabled={Boolean(selectedInvoice?.currency)}
              >
                {(currencies || []).map((c) => {
                  const code = c.code || c.currency_code || c;
                  return (
                    <option key={code} value={code}>
                      {code}
                      {c.name ? ` — ${c.name}` : ''}
                    </option>
                  );
                })}
                {form.currency &&
                  !(currencies || []).some(
                    (c) => (c.code || c.currency_code) === form.currency
                  ) && <option value={form.currency}>{form.currency}</option>}
                {(!currencies || currencies.length === 0) &&
                  ['USD', 'INR', 'CAD', 'EUR', 'GBP'].map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
              </select>
              <p className="erp-panel-meta">
                {selectedInvoice?.currency
                  ? `Locked to invoice currency (${selectedInvoice.currency}).`
                  : 'Select an invoice to set currency, or choose from the list.'}
              </p>
            </div>
            <div className="erp-field">
              <label htmlFor="cn-amount">Amount ({form.currency || 'USD'})</label>
              <input
                id="cn-amount"
                type="number"
                step="0.01"
                min="0.01"
                inputMode="decimal"
                required
                placeholder="0.00"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
            </div>
            <div className="erp-field">
              <label htmlFor="cn-date">Credit date</label>
              <input
                id="cn-date"
                type="date"
                value={form.credit_date}
                onChange={(e) => setForm({ ...form, credit_date: e.target.value })}
              />
            </div>
            <div className="erp-field" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="cn-reason">Reason</label>
              <input
                id="cn-reason"
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="Scope reduction / billing correction"
              />
            </div>
          </div>

          <div className="svc-package-page-actions">
            <button type="button" className="btn btn-secondary" onClick={backToList}>
              Cancel
            </button>
            <LoadingButton
              type="submit"
              className="btn btn-primary"
              loading={createMutation.isLoading}
              disabled={!form.sales_document_id || !Number(form.amount)}
            >
              Create credit note
            </LoadingButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreditNoteDetails;
